import { useState, useEffect } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { NAV } from '@/constants/testIds';

const links = [
  { to: '/', label: 'Home', testid: NAV.home },
  { to: '/about', label: 'About', testid: NAV.about },
  { to: '/services', label: 'Services', testid: NAV.services },
  { to: '/portfolio', label: 'Portfolio', testid: NAV.portfolio },
  { to: '/eureka1', label: 'Eureka1', testid: NAV.eureka1 },
  { to: '/blog', label: 'Journal', testid: NAV.blog },
  { to: '/contact', label: 'Contact', testid: NAV.contact },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? 'bg-[#F4EFEA]/95 backdrop-blur border-b border-[#E6CFC7]' : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="flex items-center justify-between h-20 lg:h-24">
          <Link to="/" data-testid={NAV.logo} className="group flex items-center gap-3">
            <img
              src="https://customer-assets.emergentagent.com/job_ops-excellence-28/artifacts/vbmsfc2r_image.png"
              alt="Eureka1"
              className="h-9 lg:h-11 w-auto"
            />
            <span className="hidden sm:inline font-serif-italic italic text-sm text-[#1C1C1C]/60">by Hallie</span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testid}
                className={({ isActive }) =>
                  `text-[13px] tracking-[0.08em] uppercase font-medium gold-underline transition-colors ${
                    isActive ? 'text-[#C9A24D]' : 'text-[#1C1C1C] hover:text-[#1C1C1C]'
                  }`
                }
                end={l.to === '/'}
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:block">
            <Link to="/contact" data-testid={NAV.cta} className="btn-primary">
              Book a Discovery Call
            </Link>
          </div>

          <button
            data-testid={NAV.mobileToggle}
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-[#1C1C1C]"
            aria-label="Toggle menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-[#F4EFEA] border-t border-[#E6CFC7]">
          <div className="px-6 py-8 flex flex-col gap-5">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testid}-mobile`}
                className={({ isActive }) =>
                  `text-base tracking-wide ${isActive ? 'text-[#C9A24D]' : 'text-[#1C1C1C]'}`
                }
                end={l.to === '/'}
              >
                {l.label}
              </NavLink>
            ))}
            <Link to="/contact" className="btn-primary mt-2 self-start" data-testid={`${NAV.cta}-mobile`}>
              Book a Discovery Call
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
